import React, { useEffect, useState } from 'react'
import { uploadFileToS3 } from '@iheartjane/react-image-list'
import { API_BRAND_LOGO_S3_UPLOAD } from '../../consts'
import { Container, Input, Image } from 'semantic-ui-react'
import { BrandSearch } from '../BrandSearch/BrandSearch'
import { Brand } from '../../redux/types/products'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from '../../redux/store'
import { getBrands } from '../../redux/actions/products'
import { BlockPicker, ColorResult } from 'react-color'
import axios, { AxiosResponse } from 'axios'
import { Field, Form } from 'react-final-form'
import PbtCustomImage from './PbtCustomImage'
import PbtCustomVideo from './PbtCustomVideo'

const BrandThemes = () => {
  const source = useSelector((state: AppState) => state.products.brands)
  const [brandId, setBrandId] = useState<number | null>(null)
  const [initialValues, setInitialValues] = useState(null)
  const [savedCount, setSavedCount] = useState(0)
  const dispatch = useDispatch()
  const CUSTOM_IMAGE_COUNT = 5

  useEffect(() => {
    dispatch(getBrands())
  }, [dispatch])

  useEffect(() => {
    if (brandId) {
      axios.get('/api/product-brand-theme/' + brandId).then((_result: any) => {
        const result = _result.data['product_brand_theme']
        if (result?.id) {
          result['pbt_custom_images_attributes'] = [...Array(CUSTOM_IMAGE_COUNT).keys()].map(
            (i) => ({
              position: i,
            })
          )
          // Rails accepts nested attributes with _attributes suffix
          // but it returns them without the suffix
          if (result?.pbt_custom_images) {
            result.pbt_custom_images.forEach((image: any) => {
              result['pbt_custom_images_attributes'][image.position] = image
            })
          }
          result['pbt_custom_video_attributes'] = result['pbt_custom_video']
          delete result['pbt_custom_images']
          delete result['pbt_custom_video']
          setInitialValues(result)
        }
      })
    } else {
      setInitialValues(null)
    }
  }, [brandId, savedCount])

  const uploadFile = async (file: File, isBrandLogo: boolean) => {
    const fd = new FormData()
    fd.append('image', file)
    fd.append('type', file.type)
    fd.append('filename', file.name)
    fd.append('product_brand_theme_image_type', isBrandLogo ? 'brand_logo' : 'custom_image')

    return uploadFileToS3(API_BRAND_LOGO_S3_UPLOAD, file, {
      credentials: 'include',
      method: 'post',
      body: fd,
    })
  }

  const handleFileChange = async (e: any, callback: any, isBrandlogo: boolean) => {
    if (e.target?.files?.length) {
      try {
        const result = await uploadFile(e.target.files[0], isBrandlogo)
        callback(result)
      } catch (e) {
        alert('Image does not meet requirements')
        return null
      }
    }
  }

  const ColorBlockPickerAdapter = ({ input: { onChange, value }, label, ...rest }: any) => (
    <>
      <label>{label}</label>
      <BlockPicker
        {...rest}
        color={value}
        onChangeComplete={(color: ColorResult) => onChange(color.hex)}
        triangle="hide"
      />
    </>
  )

  const ImageAdapter = ({
    input: { onChange, value },
    label,
    isBrandLogo,
    required,
    ...rest
  }: any) => {
    return (
      <>
        <label>{label}</label>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <Input
            {...rest}
            type="file"
            onChange={(e) => handleFileChange(e, onChange, isBrandLogo)}
            required={required && !value}
          />
          <Image width={200} src={value} />
        </div>
      </>
    )
  }

  const BrandThemeForm = ({ initialValues }: any) => (
    <Form
      onSubmit={(formData) => {
        const toSubmit: any = {
          product_brand_theme: { ...formData, product_brand_id: brandId },
        }

        // Don't include pbt_custom_video_attributes if overlay_image_url is not present
        if (!toSubmit.product_brand_theme.pbt_custom_video_attributes.overlay_image_url) {
          delete toSubmit.product_brand_theme.pbt_custom_video_attributes
        }

        // Put all non-deleted images at the front of the list
        toSubmit.product_brand_theme.pbt_custom_images_attributes =
          formData?.pbt_custom_images_attributes
            .filter((image: any) => image.image_url?.length && !image._destroy)
            .sort((a: any, b: any) => a.position - b.position)
            .map((image: any, i: number) => ({ ...image }))

        // Set customImageUrl equal to the first not-deleted image_url
        let customImageUrl = null
        const { pbt_custom_images_attributes } = toSubmit.product_brand_theme
        if (pbt_custom_images_attributes?.length > 0) {
          customImageUrl = pbt_custom_images_attributes[0].image_url
        }

        // Put all deleted images at the end of the list. Position left unchanged
        toSubmit.product_brand_theme.pbt_custom_images_attributes =
          toSubmit.product_brand_theme?.pbt_custom_images_attributes?.concat([
            ...formData?.pbt_custom_images_attributes?.filter((image: any) => image._destroy),
          ])

        toSubmit.product_brand_theme.custom_image_url = customImageUrl
        return new Promise<void>((resolve, reject) => {
          axios
            .post('/api/product-brand-theme', toSubmit)
            .then((ar: AxiosResponse) => {
              alert('Saved!')
              resolve()
            })
            .catch(() => {
              alert('Error!')
              reject()
            })
            .finally(() => {
              setSavedCount(savedCount + 1)
            })
        })
      }}
      initialValues={initialValues}
      validate={(values) => {
        const errors: any = {}
        const customVideo = {
          video_url: values?.pbt_custom_video_attributes?.video_url,
          overlay_image_url: values?.pbt_custom_video_attributes?.overlay_image_url,
          _destroy: values?.pbt_custom_video_attributes?._destroy,
        }
        if (
          ((customVideo.overlay_image_url && !customVideo.video_url) ||
            (!customVideo.overlay_image_url && customVideo.video_url)) &&
          !customVideo._destroy
        ) {
          errors.pbt_custom_video_attributes = 'must set overlay image and video URL'
        }

        ;[...Array(CUSTOM_IMAGE_COUNT).keys()].forEach((i: number) => {
          const customImage = {
            image_url: values?.pbt_custom_images_attributes?.[i]?.image_url,
            cta_text: values?.pbt_custom_images_attributes?.[i]?.cta_text,
            cta_url: values?.pbt_custom_images_attributes?.[i]?.cta_url,
            description: values?.pbt_custom_images_attributes?.[i]?.description,
            _destroy: values?.pbt_custom_images_attributes?.[i]?._destroy,
          }

          if (
            (customImage.cta_text || customImage.cta_url || customImage.description) &&
            !customImage.image_url &&
            !customImage._destroy
          ) {
            errors[`pbt_custom_images_attributes[${i}]`] = 'must set image URL'
          }
        })

        return errors
      }}
      render={({ handleSubmit, form, submitting, pristine, values, errors }) => (
        <>
          <form onSubmit={handleSubmit} className="ui form">
            <div className="field">
              <label>Rank (optional)</label>
              <Field name="rank" component="input" type="number" />
            </div>
            <div className="field">
              <label>Description. Optional. Max 500 characters.</label>
              <Field name="description" component="textarea" />
            </div>
            <div className="field">
              <Field
                required
                name="color_hex"
                label="Color (required)"
                initialValue="#FFFFFF"
                component={ColorBlockPickerAdapter}
              />
            </div>
            <div className="field">
              <Field
                required
                name="logo_url"
                label="Brand Logo. Required. Must be 1:1 aspect ratio and at least 400x400px. JPG, PNG, SVG ok."
                isBrandLogo={true}
                component={ImageAdapter}
              />
            </div>

            <div>
              {[...Array(CUSTOM_IMAGE_COUNT).keys()].map((i) => {
                return (
                  <PbtCustomImage
                    showDestroy={initialValues?.pbt_custom_images_attributes?.[i]?.id}
                    index={i}
                    key={i}
                    imageAdapter={ImageAdapter}
                    hasError={!!errors[`pbt_custom_images_attributes[${i}]`]}
                  />
                )
              })}

              <div>
                <PbtCustomVideo
                  hasError={!!errors?.pbt_custom_video_attributes}
                  showDestroy={initialValues?.pbt_custom_video_attributes?.id}
                  imageAdapter={ImageAdapter}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting || !!Object.keys(errors)?.length}
              className="ui button fluid primary"
            >
              Save
            </button>
          </form>
        </>
      )}
    />
  )

  return (
    <Container>
      <h1>Brand Theme</h1>
      <div style={{ maxWidth: '300px' }}>
        <BrandSearch
          hideCreate={true}
          placeholder="Select a brand.."
          noSelectionMessage="No brand selected"
          onSelect={(result: Brand | null) => {
            if (result) {
              setBrandId(result.id)
            } else {
              setBrandId(null)
            }
          }}
          source={source}
          currentBrandId={brandId || ''}
          name={'brand-selector'}
        />
      </div>

      {brandId && <BrandThemeForm initialValues={initialValues} />}
    </Container>
  )
}

export default BrandThemes
